import { partial } from 'lodash';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button } from '@vimily/bonjoro-ui-components-v2/Button';
import {
  MeatballMenu,
  MeatballMenuItemProps,
} from '@vimily/bonjoro-ui-components-v2/MeatballMenu';

import Modal from '~/App/Components/Modal';
import { Greet } from '~/types/models';
import TextContainer from './Shared/TextContainer';
import { getProfileName } from '~/BonjorosList/util';
import updateGreetProfileAction from '~/actions/greets/updateGreetProfileAction';
import { first } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useDeleteGreets } from '~/react-hooks/use-delete-greets';

export interface ProfileHeaderProps {
  greet: Greet;
  className?: string;
}

interface GreetProfileFormData {
  email: string;
  firstName: string;
  lastName: string;
  note: string;
}

type UpdateGreetProfileMutation = GreetProfileFormData & {
  greetId: string;
  profileId?: string;
};

function ProfileHeader({ greet, className }: ProfileHeaderProps) {
  const { profiles } = greet;
  const profile = first(profiles);

  const [editModalIsOpen, setEditModalOpen] = useState(false);
  const { register, handleSubmit } = useForm<GreetProfileFormData>();
  const queryClient = useQueryClient();
  const { handleDeleteClick } = useDeleteGreets([greet]);

  const updateGreetProfileFn = async ({
    greetId,
    profileId,
    email,
    firstName,
    lastName,
    note,
  }: UpdateGreetProfileMutation) => {
    const res = await updateGreetProfileAction({
      greetId,
      profileId,
      email,
      firstName,
      lastName,
      note,
    });

    return res.data.greets;
  };

  const { mutateAsync: updateGreetProfile, isLoading: loading } = useMutation(
    updateGreetProfileFn,
    {
      onSuccess: (data: Greet, { greetId }) => {
        queryClient.setQueryData(['greet', { id: greetId }], data);
      },
    }
  );

  const onSubmit: SubmitHandler<GreetProfileFormData> = async (
    formData,
    event
  ) => {
    event?.preventDefault();

    await updateGreetProfile({
      greetId: greet.id,
      profileId: profile?.id,
      ...formData,
    });

    setEditModalOpen(false);
  };

  const onCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setEditModalOpen(false);
  };

  const profileMeatballMenuItems: MeatballMenuItemProps[] = [
    {
      logo: <i className='icon icon-sm icon-edit-contact' />,
      label: 'Edit contact',
      onClick: partial(setEditModalOpen, true),
    },
    {
      logo: <FontAwesomeIcon icon={faTrash} className='!w-4 !h-4' />,
      label: 'Dismiss',
      className: 'v2-text-red-700',
      onClick: handleDeleteClick,
    },
  ];
  return (
    <div className={className}>
      <TextContainer>
        <div className='p-0 m-0 flex items-center'>
          {profile && (
            <span className='text-[21px] text-neutral-900 font-bold'>
              {getProfileName(profile)}
            </span>
          )}
          <MeatballMenu
            bodyClassName='ml-5'
            items={profileMeatballMenuItems}
            align='start'
          />
          <Modal
            isOpen={editModalIsOpen}
            onRequestClose={partial(setEditModalOpen, false)}
            hideActions
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='form-group'>
                <label>Email Address</label>
                <input
                  className='form-control'
                  type='email'
                  name='email'
                  defaultValue={profile?.email}
                  ref={register}
                />
              </div>
              <div className='form-group'>
                <label>First Name</label>
                <input
                  className='form-control'
                  name='firstName'
                  defaultValue={profile?.first_name ?? ''}
                  ref={register}
                />
              </div>
              <div className='form-group'>
                <label>Last Name</label>
                <input
                  className='form-control'
                  name='lastName'
                  defaultValue={profile?.last_name ?? ''}
                  ref={register}
                />
              </div>
              <div className='form-group'>
                <label>Task Identifier</label>
                <input
                  className='form-control'
                  name='note'
                  defaultValue={greet.event?.message ?? ''}
                  ref={register}
                />
              </div>

              <Button className='border-solid' disabled={loading}>
                {!loading && 'Save'}
                {loading && (
                  <>
                    {'Saving '}
                    <i className='far fa-spinner fa-spin' />
                  </>
                )}
              </Button>
              <Button
                variant='info'
                className='border-solid ml-2'
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            </form>
          </Modal>
        </div>
      </TextContainer>
    </div>
  );
}

export default ProfileHeader;
